import React, { useMemo } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import './BlogContentEditor.css';

export type BlogContentEditorProps = {
  value: string;
  onChange: (html: string) => void;
  id?: string;
};

/**
 * Rich text body for blog posts: format with toolbar or paste from Word/Google Docs.
 * Output is HTML stored in Firestore and sanitized on save + on public render.
 */
const BlogContentEditor: React.FC<BlogContentEditorProps> = ({ value, onChange, id }) => {
  const modules = useMemo(
    () => ({
      toolbar: {
        container: [
          [{ header: [1, 2, 3, false] }],
          ['bold', 'italic', 'underline', 'strike'],
          [{ list: 'ordered' }, { list: 'bullet' }, { indent: '-1' }, { indent: '+1' }],
          ['blockquote', 'code-block'],
          [{ align: [] }],
          ['link'],
          ['clean'],
        ],
      },
      clipboard: {
        matchVisual: false,
      },
    }),
    []
  );

  const formats = useMemo(
    () => [
      'header',
      'bold',
      'italic',
      'underline',
      'strike',
      'list',
      'bullet',
      'indent',
      'blockquote',
      'code-block',
      'align',
      'link',
    ],
    []
  );

  return (
    <div className="blog-content-editor-wrap">
      <label className="blog-content-editor-label" htmlFor={id}>
        Article body
      </label>
      <ReactQuill
        id={id}
        theme="snow"
        value={value}
        onChange={onChange}
        modules={modules}
        formats={formats}
        placeholder="Write here, or paste from Word, Google Docs, or email — headings, lists, and line breaks are kept."
      />
      <p className="blog-content-editor-hint">
        Use the toolbar for headings and lists, or paste directly: formatting from Word and Google Docs is preserved
        where possible. Avoid pasting huge images; use the featured image field for the cover.
      </p>
    </div>
  );
};

export default BlogContentEditor;
